import useAnalytics from '@hooks/useAnalytics';
import { useIsChooseDoctorFlow } from '@hooks/useIsChooseDoctorFlow';
import { fetchClinics, fetchNextAvailable } from '@packages/cortex-sdk';
import { useNavigationStore, useServiceTreeStore } from '@packages/shared';
import { Actions, Clinic } from '@packages/types';
import { AnimatedStep, StepButton, StepWrapper } from '@packages/ui-web';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';

export default function SelectLocationStep() {
  const { queries, setQueries, advanceStep, goToStep } = useNavigationStore(
    (state) => ({
      queries: state.queries,
      setQueries: state.setQueries,
      advanceStep: state.advanceStep,
      goToStep: state.goToStep,
    }),
  );
  const { service, speciality, vertical } = queries;
  const setEventProps = useAnalytics();

  const { getClinicIdsFromServiceTree, getServiceUuid } = useServiceTreeStore(
    (state) => ({
      getClinicIdsFromServiceTree: state.getClinicIdsFromServiceTree,
      getServiceUuid: state.getServiceUuid,
    }),
  );

  const serviceId = service ? getServiceUuid(service) ?? '' : '';
  const servicesInClinics = service ? getClinicIdsFromServiceTree(service) : [];
  const isChooseGpPractitionerFlow = useIsChooseDoctorFlow();

  useEffect(() => {
    if (isChooseGpPractitionerFlow) {
      goToStep(4);
    }
  }, [isChooseGpPractitionerFlow, goToStep]);

  const { data, isLoading } = useQuery<Clinic[]>(
    ['clinics'],
    ({ signal }) => fetchClinics({ serviceId, signal }),
    {
      enabled: !!serviceId,
      select: (data) => {
        const deduped: Clinic[] = [];

        for (const clinic of data) {
          if (
            clinic.address.city &&
            !deduped.find((c) => clinic.address.city === c.address.city)
          ) {
            deduped.push(clinic);
          }
        }
        return deduped;
      },
    },
  );

  const queryClient = useQueryClient();

  useEffect(
    () =>
      servicesInClinics?.forEach((clinicId) =>
        queryClient.prefetchQuery(
          ['next-available', clinicId, serviceId],
          ({ signal }) =>
            fetchNextAvailable({
              clinicId,
              serviceId,
              signal,
            }),
        ),
      ),
    [servicesInClinics],
  );

  const { t } = useTranslation('booking');

  const handleClick = (city: string) => {
    advanceStep();
    setQueries({ city });
    setEventProps({
      event: {
        action: Actions.SelectLocation,
        vertical,
        service,
        speciality,
        city,
      },
    });
  };

  return (
    <StepWrapper
      title={t('steps.select_area.title')}
      isLoading={isLoading}
      stepWidth="max-w-[512px]"
    >
      {(data ?? [])?.map(({ address }, index) => (
        <AnimatedStep key={`${address.city}_${index}`} order={index}>
          <StepButton
            title={address.city}
            onClick={() => {
              handleClick(address.city);
            }}
          />
        </AnimatedStep>
      ))}
    </StepWrapper>
  );
}
